import { Section } from "@redotech/redo-model/email-builder";
import { ProductFilter } from "@redotech/redo-model/product-filters/product-filters-db-parser";
import {
  ProductRecommendationType,
  UniqueProductFilterType,
  comparisonOperatorEvaluator,
} from "@redotech/redo-model/product-filters/product-filters-types";
import {
  ProductRecommendationIntent,
  ProductSortKeys,
} from "@redotech/shopify-client/storefront.graphql";
import { filterTruthy } from "@redotech/util/array";
import {
  Gid,
  ShopifyIdType,
  convertShopifyIdToGid,
} from "@redotech/util/shopify-id";
import { assertNever } from "@redotech/util/type";
import {
  PRODUCT_INFO_FRAGMENT,
  ProductInfo,
  getCollection,
  getCollections,
  getProduct,
  getProductWithMetafield,
  getProducts,
  getProductsByGids,
} from "./products";
import {
  GetProductRecommendationsQuery,
  GetProductRecommendationsQueryVariables,
} from "./shopify-storefront.graphql";
import { ShopifyStorefrontClient, handleErrors } from "./storefront-client";

const getProductRecommendationsQuery = /* GraphQL */ `
  ${PRODUCT_INFO_FRAGMENT}
  query getProductRecommendations(
    $productId: ID!
    $intent: ProductRecommendationIntent!
  ) {
    productRecommendations(productId: $productId, intent: $intent) {
      ...ProductInfo
    }
  }
`;

export const getProductRecommendations = async (
  client: ShopifyStorefrontClient,
  productId: Gid,
): Promise<ProductInfo[]> => {
  const { data, errors } = await client.request<
    GetProductRecommendationsQuery,
    GetProductRecommendationsQueryVariables
  >(getProductRecommendationsQuery, {
    productId: productId,
    intent: ProductRecommendationIntent.Related,
  });

  handleErrors(errors);

  return data?.productRecommendations || [];
};

const validProduct = (product: ProductInfo) =>
  product.vendor !== "re:do" && product.variants.nodes?.[0]?.requiresShipping;

export async function getRecommendationsForMultipleProducts(
  storefrontClient: ShopifyStorefrontClient,
  productIDs: Gid[],
  numRecommendations: number,
  productIdsToExclude: Gid[] = [],
): Promise<ProductInfo[]> {
  const originalProducts = new Set(productIDs);
  const recommendations = (
    await Promise.all(
      productIDs.map(async (id: any) => {
        try {
          return await getProductRecommendations(storefrontClient, id);
        } catch (e) {
          console.error("Error getting product recommendations", e);
          return [];
        }
      }),
    )
  )
    .flat()
    .filter((p) => p.availableForSale);

  const counts = recommendations
    .filter(validProduct)
    .reduce(
      (
        acc: Record<string, { count: number; product: ProductInfo }>,
        product,
      ) => {
        if (!acc[product.id]) {
          acc[product.id] = { count: 0, product };
        }
        acc[product.id].count++;
        return acc;
      },
      {},
    );

  const topRecommendations = Object.entries(counts)
    .filter(
      ([id]) =>
        !productIdsToExclude.includes(
          convertShopifyIdToGid(id, ShopifyIdType.Product),
        ),
    )
    .sort((a, b) => b[1].count - a[1].count)
    .slice(0, numRecommendations)
    .map(([_, { product }]) => product);

  try {
    // If we didn't find enough recommendations,
    // fill the rest of the list with the best sellers.
    if (topRecommendations.length < numRecommendations) {
      const iterator = getProducts(storefrontClient, {
        first: numRecommendations,
        query: `-vendor:re\\:do`,
        sortKey: ProductSortKeys.BestSelling,
      });
      for await (const product of iterator) {
        if (topRecommendations.length >= numRecommendations) {
          break;
        }
        // Don't push products we're already recommending,
        // or products we are trying to make recommendations for.
        if (
          validProduct(product) &&
          !counts[product.id] &&
          !originalProducts.has(
            convertShopifyIdToGid(product.id, ShopifyIdType.Product),
          ) &&
          product.availableForSale
        ) {
          counts[product.id] = { count: 1, product };
          topRecommendations.push(product);
        }
      }
    }
  } catch (e) {
    console.error("Error getting top products", e);
  }
  return topRecommendations;
}

export const getProductsByTags = async (
  client: ShopifyStorefrontClient,
  tags: string[],
  amount: number = 250,
  sortKey: ProductSortKeys = ProductSortKeys.BestSelling,
): Promise<RecommendedProduct[]> => {
  const products: ProductInfo[] = [];
  try {
    const iterator = getProducts(client, {
      first: amount,
      query: `tag:${tags.map((tag: string) => `"${tag}"`).join(" OR ")}`,
      sortKey: sortKey,
    });
    for await (const product of iterator) {
      products.push(product);
      if (products.length >= amount) {
        break; // Stop iterating once we reach the desired amount
      }
    }
  } catch (e) {
    console.error("Error getting top products", e);
  }
  const productsWithImageAndPrice = products
    .map((recommendation) => {
      return {
        ...recommendation,
        altImage: {
          url: recommendation!.images.nodes[1]?.url,
          altText: recommendation!.images.nodes[1]?.altText ?? "",
        },
        price: recommendation!.priceRange.minVariantPrice.amount,
      };
    })
    .filter((p) => !!p);

  return productsWithImageAndPrice;
};

export const getProductsByCollection = async (
  client: ShopifyStorefrontClient,
  collectionId: Gid,
  numRecommendations: number,
): Promise<RecommendedProduct[]> => {
  const collection = await getCollection(client, collectionId);
  if (!collection) {
    throw new Error("Collection not found");
  }

  const items = collection.products.nodes
    .map((product: ProductInfo) => {
      return {
        ...product,
        altImage: {
          url: product?.images?.nodes?.[1]?.url,
          altText: product?.images?.nodes?.[1]?.altText ?? "",
        },
        price: product.priceRange.minVariantPrice.amount,
      };
    })
    .slice(0, numRecommendations);
  return items;
};

export const getProductsByCollections = async (
  client: ShopifyStorefrontClient,
  collectionIds: Gid[],
  numRecommendations: number,
): Promise<RecommendedProduct[]> => {
  const collections = await getCollections(client, collectionIds);
  const products = collections
    .flatMap((collection) => collection?.products.nodes ?? [])
    .filter((product) => product.availableForSale);
  const items = products
    .map((product: ProductInfo) => {
      return {
        ...product,
        altImage: {
          url: product?.images?.nodes?.[1]?.url,
          altText: product?.images?.nodes?.[1]?.altText ?? "",
        },
        price: product.priceRange.minVariantPrice.amount,
      };
    })
    .slice(0, numRecommendations);
  return items;
};

export const getProductsById = async (
  client: ShopifyStorefrontClient,
  // When a product id is passed in as a part of a query, it is expected to be the numeric id, not the gid.
  productIds: Gid[],
): Promise<ProductInfo[]> => {
  try {
    const products = await getProductsByGids(client, productIds);
    return products;
  } catch (e) {
    console.error("Error getting products by id", e);
    return [];
  }
};

export const getProductsByShopifyRecommendations = async (
  storefrontClient: any,
  productIds: Gid[],
  numRecommendations: number = 15,
  filterCurrentLineItems: boolean = false,
): Promise<RecommendedProduct[]> => {
  // get extra recommended products so that if any have low inventory, we can filter them out
  const recommendations = (
    await getRecommendationsForMultipleProducts(
      storefrontClient,
      productIds,
      numRecommendations,
      filterCurrentLineItems ? productIds : [],
    )
  ).map((p) => {
    return {
      ...p,
      altImage: {
        url: p?.images?.nodes?.[1]?.url,
        altText: p?.images?.nodes?.[1]?.altText ?? "",
      },
      price: p.priceRange.minVariantPrice.amount,
    };
  });
  return recommendations;
};

export const getProductsByProductMetafieldInOrder = async (
  storefrontClient: ShopifyStorefrontClient,
  productIds: Gid[],
  metafield: { key: string; namespace: string; metaobjectKey?: string },
  numRecommendations: number,
  filterCurrentLineItems: boolean = false,
): Promise<RecommendedProduct[]> => {
  if (!metafield?.key || !metafield?.namespace) {
    console.error("Metafield key or namespace not found in settings");
    return getProductsByShopifyRecommendations(
      storefrontClient,
      productIds,
      numRecommendations,
      filterCurrentLineItems,
    );
  }

  const productsWithMetafields = await successfulResults(productIds, (id) =>
    getProductWithMetafield(
      storefrontClient,
      id,
      metafield.key,
      metafield.namespace,
      metafield.metaobjectKey,
    ),
  );

  const recommendations = await successfulResults(
    productsWithMetafields,
    (product) => getProduct(storefrontClient, product.id),
  );

  const items = recommendations
    .map((recommendation) => {
      return {
        ...recommendation,
        altImage: {
          url: recommendation!.images.nodes[1]?.url,
          altText: recommendation!.images.nodes[1]?.altText ?? "",
        },
        // TODO: make "price" both amount and currencyCode throughout this file
        price: recommendation!.priceRange.minVariantPrice.amount,
      };
    })
    .filter((p) => !!p)
    .filter((p) =>
      filterCurrentLineItems
        ? !productIds.includes(
            convertShopifyIdToGid(p.id, ShopifyIdType.Product),
          )
        : true,
    );

  if (items.length < numRecommendations) {
    // Couldn't pull enough products from metafields, filling in remaining with recommendations
    const recommendedProducts = await getProductsByShopifyRecommendations(
      storefrontClient,
      productIds,
      numRecommendations - items.length,
      filterCurrentLineItems,
    );
    const uniqueRecommendedProducts = recommendedProducts.filter(
      (p) => !items.some((item) => item.id === p.id),
    );
    const itemsWithExtra = [
      ...items,
      ...uniqueRecommendedProducts.map((p) => ({
        ...p,
        altImage: {
          url: p?.images?.nodes?.[1]?.url,
          altText: p?.images?.nodes?.[1]?.altText ?? "",
        },
        price: p.priceRange.minVariantPrice.amount,
      })),
    ];
    return itemsWithExtra;
  }
  return items;
};

function isSuccessful<T>(
  result: PromiseSettledResult<T>,
): result is PromiseFulfilledResult<T> {
  return result.status === "fulfilled" && !!result.value;
}

async function successfulResults<A, B>(
  inputValues: A[],
  fn: (value: A) => Promise<B>,
): Promise<NonNullable<B>[]> {
  return (await Promise.allSettled(inputValues.map(fn)))
    .filter(isSuccessful)
    .map((result) => result.value as NonNullable<B>);
}

export async function getManualProducts({
  storeUrl,
  storefrontAccessToken,
  sectionProps,
}: {
  storeUrl: string;
  storefrontAccessToken: string | null | undefined;
  sectionProps: Section.Products;
}): Promise<RecommendedProduct[]> {
  const productIds =
    sectionProps.manuallySelectedProducts?.map((p) => p?.productId) ?? [];

  if (!productIds.length || !storefrontAccessToken) {
    return [];
  }
  const client = new ShopifyStorefrontClient(storeUrl, storefrontAccessToken!);

  const productsById = await getProductsById(
    client,
    filterTruthy(productIds).map((id) =>
      convertShopifyIdToGid(id, ShopifyIdType.Product),
    ),
  );
  const result: RecommendedProduct[] = productsById.map((p) => ({
    ...p,
    altImage: {
      url: p?.images?.nodes?.[1]?.url,
      altText: p?.images?.nodes?.[1]?.altText ?? "",
    },
    price: p.priceRange.minVariantPrice.amount,
  }));
  return result.filter((product) => product.vendor !== "re:do");
}

export type RecommendedProduct = ProductInfo & {
  altImage: { url?: string; altText?: string };
  price: string;
};

export const getProductsByProductRecommendationFilter = async (
  client: ShopifyStorefrontClient,
  productRecommendationFilter: ProductFilter,
  productIds: Gid[],
): Promise<RecommendedProduct[]> => {
  let products: RecommendedProduct[] = [];
  switch (productRecommendationFilter.productRecommendationType) {
    case ProductRecommendationType.TAG:
      products = await getProductsByTags(
        client,
        productRecommendationFilter.tags,
        10,
      );
      break;
    case ProductRecommendationType.COLLECTION:
      products = await getProductsByCollection(
        client,
        convertShopifyIdToGid(
          productRecommendationFilter.collectionId,
          ShopifyIdType.Collection,
        ),
        10,
      );
      break;
    case ProductRecommendationType.METAFIELD: {
      const metafield = productRecommendationFilter.metafield;
      if (!metafield) {
        throw new Error("Metafield not found");
      }
      products = await getProductsByProductMetafieldInOrder(
        client,
        productIds,
        metafield,
        10,
      );
      break;
    }
    case ProductRecommendationType.PROVIDER_RECOMMENDATIONS:
      products = await getProductsByShopifyRecommendations(
        client,
        productIds,
        10,
        true,
      );
      break;
    default:
      throw new Error("Unsupported product recommendation filter type");
  }

  //TODO: these should really be added as filters to the queries above
  const filteredProducts = products.filter((product) =>
    // If there are no additional filters, keep all products
    !productRecommendationFilter.additionalProductFilters?.length
      ? true
      : productRecommendationFilter.additionalProductFilters?.some((filter) => {
          switch (filter.type) {
            case UniqueProductFilterType.TAG:
              return filter.tags.some((tag) => product.tags.includes(tag));
            case UniqueProductFilterType.COLLECTION:
              return filter.collectionIds.some((collectionId) =>
                product.collections.nodes.some(
                  (collection) => collection.id === collectionId,
                ),
              );
            case UniqueProductFilterType.METAFIELD:
              return filter.metafields.some(
                (metafield) =>
                  //TODO we need to add metafield support here, currently, the storefront api does not support metafields in the search query
                  true,
              );
            case UniqueProductFilterType.INVENTORY:
              return comparisonOperatorEvaluator[filter.comparisonOperator](
                product.variants.nodes.reduce(
                  (acc, variant) => acc + (variant.quantityAvailable ?? 0),
                  0,
                ),
                filter.inventory,
              );
            case UniqueProductFilterType.PRICE:
              return comparisonOperatorEvaluator[filter.comparisonOperator](
                product.priceRange.minVariantPrice.amount,
                filter.price,
              );
            default:
              assertNever(filter);
          }
        }),
  );

  return filteredProducts;
};
